// Row-level checks: normalize a row, compute Google's cut once, lay it out on each profile.
// Browser only (uses the engine). The pure assembly into warnings lives in assemble.ts.

import type { FieldResult, InputRow, Profile, SerpSizeConfig } from './types';
import { googleCut, layoutProfile } from './engine';
import { missingFamilies } from './fonts';
import { normalize } from './text';
import type { ProfileSet } from './profiles';
import { FIELDS, assembleRow, rowError, rowUrl, type EngineField, type EngineRow, type RowCuts } from './rows';

export { FIELDS, assembleRow, mergeEngineRows, rowError, rowUrl, type EngineField, type EngineRow, type RowCuts } from './rows';

export class Checker {
  constructor(
    readonly profiles: ProfileSet,
    readonly config: SerpSizeConfig,
  ) {}

  /** Check one row on the given profiles. `cuts` (from Chromium) override the local step-1 computation. */
  checkRow(row: InputRow, url: string, profiles: Profile[], cuts?: RowCuts): EngineRow {
    const err = rowError(row);
    if (err) return { url, error: err, fields: [] };
    const fields: EngineField[] = [];
    for (const field of FIELDS) {
      const original = normalize(row[field]);
      if (!original) continue;
      const cut = cuts?.[field] ?? googleCut(field, original, this.config);
      const results = profiles.map(p =>
        layoutProfile(field, original, cut, p, this.config, {
          query: row.query,
          twin: p.fontTwin ? this.profiles.get(p.fontTwin) : undefined,
          missingFamilies,
        }),
      );
      fields.push({ field, original, cut, results });
    }
    return { url, fields };
  }

  /** Engine + assembly in one engine (the web UI path). */
  checkPage(row: InputRow, index: number, profiles: Profile[], brand?: string): FieldResult[] {
    const url = rowUrl(row, index);
    const r = this.checkRow(row, url, profiles);
    return assembleRow(r, row, brand, this.profiles, this.config);
  }
}

